import apiClient from '../../../services/apiClient';

// Request interceptor to add auth token
apiClient.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('authToken');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

export const patientService = {
  // Get patient notifications
  async getNotifications() {
    try {
      const response = await apiClient.get('/patient/notifications');
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.detail || 'Failed to fetch notifications');
    }
  },

  // Get patient accounts/records
  async getAccounts() {
    try {
      const response = await apiClient.get('/patient/accounts');
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.detail || 'Failed to fetch accounts');
    }
  },

  // Get patient FAQs
  async getFAQs() {
    try {
      const response = await apiClient.get('/patient/faqs');
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.detail || 'Failed to fetch FAQs');
    }
  },

  // Get latest news
  async getLatestNews() {
    try {
      const response = await apiClient.get('/patient/news');
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.detail || 'Failed to fetch news');
    }
  },

  // Get upcoming bookings
  async getUpcomingBookings() {
    try {
      const response = await apiClient.get('/patient/bookings/upcoming');
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.detail || 'Failed to fetch upcoming bookings');
    }
  },

  // Create new booking
  async createBooking(bookingData) {
    try {
      const response = await apiClient.post('/patient/bookings', bookingData);
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.detail || 'Failed to create booking');
    }
  },

  // Update booking
  async updateBooking(bookingId, bookingData) {
    try {
      const response = await apiClient.put(`/patient/bookings/${bookingId}`, bookingData);
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.detail || 'Failed to update booking');
    }
  },

  // Cancel booking
  async cancelBooking(bookingId) {
    try {
      const response = await apiClient.delete(`/patient/bookings/${bookingId}`);
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.detail || 'Failed to cancel booking');
    }
  },

  // Get available time slots
  async getAvailableTimeSlots(date, doctorId) {
    try {
      const response = await apiClient.get('/patient/timeslots', {
        params: { date, doctor_id: doctorId }
      });
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.detail || 'Failed to fetch time slots');
    }
  },

  // Get all specialties
  async getSpecialties() {
    try {
      const response = await apiClient.get('/doctors/specialties');
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.detail || 'Failed to fetch specialties');
    }
  },

  // Get doctors by specialty
  async getDoctorsBySpecialty(specialty) {
    try {
      const response = await apiClient.get(`/doctors/by-specialty/${encodeURIComponent(specialty)}`);
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.detail || 'Failed to fetch doctors');
    }
  },

  // Get doctors list (legacy function)
  async getDoctors(specialty) {
    try {
      const response = await apiClient.get('/patient/doctors', {
        params: { specialty }
      });
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.detail || 'Failed to fetch doctors');
    }
  },

  // Get clinic locations
  async getClinicLocations(postCode) {
    try {
      const response = await apiClient.get('/patient/clinics', {
        params: { post_code: postCode }
      });
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.detail || 'Failed to fetch clinic locations');
    }
  },

  // Patient Bookings API calls (new endpoints)
  async createPatientBooking(bookingData) {
    try {
      console.log('📤 Sending booking data to API:', bookingData);
      const response = await apiClient.post('/patient-bookings/', bookingData);
      console.log('✅ Booking API response:', response.data);
      return response.data;
    } catch (error) {
      console.error('❌ Booking API error:', error);
      console.error('Error response data:', error.response?.data);
      console.error('Error status:', error.response?.status);
      console.error('Error headers:', error.response?.headers);
      
      // Extract detailed error information
      const errorDetail = error.response?.data?.detail || 
                         error.response?.data?.message || 
                         error.response?.data || 
                         'Failed to create booking';
      
      throw new Error(JSON.stringify(errorDetail));
    }
  },

  async getUserBookings(context = 'patient') {
    try {
      const response = await apiClient.get('/patient-bookings/', {
        params: { context }
      });
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.detail || 'Failed to fetch bookings');
    }
  },

  async getUpcomingPatientBookings(limit = 10) {
    try {
      const response = await apiClient.get(`/patient-bookings/upcoming?limit=${limit}`);
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.detail || 'Failed to fetch upcoming bookings');
    }
  },

  async getPastPatientBookings(skip = 0, limit = 100) {
    try {
      const response = await apiClient.get(`/patient-bookings/past?skip=${skip}&limit=${limit}`);
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.detail || 'Failed to fetch past bookings');
    }
  },

  async getBookingStatistics() {
    try {
      const response = await apiClient.get('/patient-bookings/statistics');
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.detail || 'Failed to fetch booking statistics');
    }
  },

  async updatePatientBooking(bookingId, updateData) {
    try {
      const response = await apiClient.put(`/patient-bookings/${bookingId}`, updateData);
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.detail || 'Failed to update booking');
    }
  },

  async cancelPatientBooking(bookingId) {
    try {
      const response = await apiClient.patch(`/patient-bookings/${bookingId}/cancel`);
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.detail || 'Failed to cancel booking');
    }
  },

  async deletePatientBooking(bookingId) {
    try {
      const response = await apiClient.delete(`/patient-bookings/${bookingId}`);
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.detail || 'Failed to delete booking');
    }
  },

  // Get bookings by patient ID (for doctors/clinicians to view patient's bookings)
  async getPatientBookingsByPatientId(patientId) {
    try {
      console.log('🔍 Service: Fetching bookings for patient ID:', patientId);
      
      // First get the patient by patient_id to get the numeric ID
      const patient = await this.getPatientByPatientId(patientId);
      if (!patient || !patient.id) {
        console.error('❌ Patient not found for ID:', patientId);
        throw new Error('Patient not found');
      }
      
      console.log('👤 Found patient:', {
        id: patient.id,
        user_id: patient.user_id,
        patient_id: patient.patient_id,
        name: `${patient.first_name} ${patient.last_name}`
      });
      
      // Try to fetch bookings - this might fail due to permissions
      try {
        const response = await apiClient.get('/patient-bookings/');
        console.log('📡 Raw booking response:', response.data);
        
        // Filter bookings for the specific patient (by user_id which matches patient.user_id)
        const allBookings = response.data;
        const patientBookings = allBookings.filter(booking => 
          booking.user_id === patient.user_id
        );
        
        console.log('📋 Filtered patient bookings:', patientBookings);
        
        // If no real bookings found, ensure sample data is returned
        if (!patientBookings || patientBookings.length === 0) {
          console.log('⚠️ No real bookings found, triggering fallback to sample data');
          throw new Error('No bookings found in API response');
        }
        
        return patientBookings;
        
      } catch (apiError) {
        console.warn('⚠️ API call failed:', apiError);
        console.error('❌ Failed to fetch patient bookings - no fallback data');
        
        // Return empty array when API fails - no more dummy data
        return [];
      }
      
    } catch (error) {
      console.error('❌ Error fetching patient bookings:', error);
      // Return empty array instead of throwing to prevent UI crashes
      return [];
    }
  },

  // Patient Preferences API calls
  async getPatientPreferences() {
    try {
      const response = await apiClient.get('/patient-preferences/');
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.detail || 'Failed to fetch patient preferences');
    }
  },

  async updatePatientPreferences(preferencesData) {
    try {
      const response = await apiClient.put('/patient-preferences/', preferencesData);
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.detail || 'Failed to update patient preferences');
    }
  },

  async createPatientPreferences(preferencesData) {
    try {
      const response = await apiClient.post('/patient-preferences/', preferencesData);
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.detail || 'Failed to create patient preferences');
    }
  },

  // Get all doctors (for preferences dropdown)
  async getAllDoctors() {
    try {
      const response = await apiClient.get('/doctors/');
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.detail || 'Failed to fetch doctors');
    }
  },

  // Get doctor by doctor_id (string) with clinic information
  async getDoctorById(doctorId) {
    try {
      const response = await apiClient.get(`/doctors/by-doctor-id/${doctorId}`);
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.detail || 'Failed to fetch doctor details');
    }
  },

  // Get doctor by numeric ID
  async getDoctorByNumericId(numericId) {
    try {
      const response = await apiClient.get(`/doctors/${numericId}`);
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.detail || 'Failed to fetch doctor details');
    }
  },

  // Get day slots for a doctor (availability API)
  async getDoctorDaySlots(doctorNumericId, isoDate) {
    try {
      const response = await apiClient.get(`/doctors/${doctorNumericId}/availability/day/${isoDate}`);
      return response.data; // [{ id, start_time, end_time, status, booking_id? }]
    } catch (error) {
      throw new Error(error.response?.data?.detail || 'Failed to fetch doctor time slots');
    }
  },

  // Alternative method for doctors to get time slots (using patient-style access)
  async getDoctorSlotsForBooking(doctorNumericId, isoDate) {
    try {
      console.log(`👥 Fetching time slots via booking endpoint for doctor ${doctorNumericId}, date ${isoDate}`);
      // Try using a different endpoint that might have different authorization rules
      const response = await apiClient.get(`/bookings/doctor-slots/${doctorNumericId}/${isoDate}`);
      console.log('✅ Booking time slots received:', response.data);
      return response.data;
    } catch (error) {
      console.error('❌ Booking time slots API error:', error);
      throw new Error(error.response?.data?.detail || 'Failed to fetch doctor time slots for booking');
    }
  },

  // Get doctor's month availability (for calendar color coding)
  async getDoctorMonthAvailability(doctorNumericId, year, month) {
    try {
      console.log(`🗓️ Fetching availability for doctor ${doctorNumericId}, ${year}-${month}`);
      const response = await apiClient.get(`/doctors/${doctorNumericId}/availability/month?year=${year}&month=${month}`);
      console.log('✅ Availability data received:', response.data);
      return response.data; // { doctor_id, year, month, days: [{ avail_date, is_available, available_slots_count }] }
    } catch (error) {
      console.error('❌ Doctor availability API error:', error);
      console.error('Error status:', error.response?.status);
      console.error('Error detail:', error.response?.data?.detail);
      
      // If it's a 403 Forbidden error, it's likely due to authorization restrictions
      // The current doctor may only be allowed to access their own availability
      if (error.response?.status === 403) {
        throw new Error('Not authorized - doctors can only access their own availability');
      }
      
      throw new Error(error.response?.data?.detail || 'Failed to fetch doctor month availability');
    }
  },

  // Alternative method for doctors to get availability (using patient-style access)
  async getDoctorAvailabilityForBooking(doctorNumericId, year, month) {
    try {
      console.log(`👥 Fetching availability via booking endpoint for doctor ${doctorNumericId}, ${year}-${month}`);
      // Try using a different endpoint that might have different authorization rules
      const response = await apiClient.get(`/bookings/doctor-availability/${doctorNumericId}?year=${year}&month=${month}`);
      console.log('✅ Booking availability data received:', response.data);
      return response.data;
    } catch (error) {
      console.error('❌ Booking availability API error:', error);
      throw new Error(error.response?.data?.detail || 'Failed to fetch doctor availability for booking');
    }
  },

  // Get clinics for a specific doctor
  async getClinicsByDoctor(doctorId) {
    try {
      const response = await apiClient.get(`/doctors/${doctorId}/clinics`);
      return response.data;
    } catch (error) {
      console.error('API Error fetching doctor clinics:', error);
      console.error('Error response:', error.response?.data);
      console.error('Doctor ID:', doctorId);
      
      // For now, return mock clinic data if API fails
      return [
        {
          id: 1,
          name: 'MediFusion Central',
          clinic_name: 'MediFusion Central',
          location: 'MediFusion Central',
          address: '123 Collins Street, Melbourne VIC 3000'
        },
        {
          id: 2,
          name: 'MediFusion North',
          clinic_name: 'MediFusion North', 
          location: 'MediFusion North',
          address: '456 High Street, Preston VIC 3072'
        }
      ];
    }
  },

  // Get all clinics
  async getAllClinics() {
    try {
      const response = await apiClient.get('/clinics/');
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.detail || 'Failed to fetch clinics');
    }
  },

  // Get clinic by ID
  async getClinicById(clinicId) {
    try {
      const response = await apiClient.get(`/clinics/${clinicId}`);
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.detail || 'Failed to fetch clinic details');
    }
  },

  // Notifications API calls
  async createNotification(notificationData) {
    try {
      const response = await apiClient.post('/notifications/', notificationData);
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.detail || 'Failed to create notification');
    }
  },

  async getNotifications(userId) {
    try {
      const response = await apiClient.get(`/notifications/user/${userId}`);
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.detail || 'Failed to fetch notifications');
    }
  },

  // Patients API calls
  async getAllPatients(search = null, skip = 0, limit = 1000) {
    try {
      const params = { skip, limit };
      if (search) {
        params.search = search;
      }
      const response = await apiClient.get('/patients/', { params });
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.detail || 'Failed to fetch patients');
    }
  },

  async getPatientById(patientId) {
    try {
      const response = await apiClient.get(`/patients/${patientId}`);
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.detail || 'Failed to fetch patient details');
    }
  },

  async getPatientByPatientId(patientId) {
    try {
      const response = await apiClient.get(`/patients/by-patient-id/${patientId}`);
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.detail || 'Failed to fetch patient details');
    }
  },

  // Get patient by user ID (includes patient_type)
  async getPatientByUserId(userId) {
    try {
      const response = await apiClient.get(`/patients/by-user/${userId}`);
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.detail || 'Failed to fetch patient details');
    }
  },

  // Create Home Care booking (for Aged Care patients)
  async createHomeCareBooking(bookingData) {
    try {
      console.log('📤 Sending Home Care booking data to API:', bookingData);
      const response = await apiClient.post('/patient-bookings/home-care', bookingData);
      console.log('✅ Home Care booking API response:', response.data);
      return response.data;
    } catch (error) {
      console.error('❌ Home Care booking API error:', error);
      console.error('Error response data:', error.response?.data);
      
      const errorDetail = error.response?.data?.detail || 
                         error.response?.data?.message || 
                         'Failed to create Home Care booking';
      
      throw new Error(errorDetail);
    }
  },

  // Patient Accounts Details API calls
  async getPatientAccountsDetailsByPatientId(patientId) {
    try {
      const response = await apiClient.get(`/patient-accounts-details/by-patient/${patientId}`);
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.detail || 'Failed to fetch patient accounts details');
    }
  }
};

export default patientService; 